import React, { useState, useEffect } from "react";
import { Eye, MapPin, User, Calendar } from "lucide-react";
import { Badge } from "@shared/ui/badge";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useConfigMapData } from "@features/onboarding/utility-setup/hooks";
import { ServiceAreaFilterPanel } from "../filter/ServiceAreaFilter";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Switch } from "@shared/ui/switch";
import { usePatchConfigMap } from "../../../hooks";

interface ServiceAreaItem {
  page_number: Number;
  count: Number;
  next: string;
  previous: string;
  result: ServiceAreaDataItem[];
}

interface ServiceAreaDataItem {
  name: string;
  latitude: number | null;
  is_active: boolean;
  longitude: number | null;
  created_by: string;
  total_units: string;
  created_date: string;
  code: string;
  child_count: number;
  configurationCode?: string; // Optional, used for display
  hierarchy_names: {
    region?: string;
    country?: string;
    state?: string;
    county?: string;
    zone?: string;
    division?: string;
    area?: string;
    sub_area?: string;
    premise?: string;
  };
  hierarchy_codes: {
    region?: string;
    country?: string;
    state?: string;
    county?: string;
    zone?: string;
    division?: string;
    area?: string;
    sub_area?: string;
    premise?: string;
  };
  installed_meter_count?: number;
  billed_unit_count?: number;
}

interface ServiceAreaFilters {
  page: number;
  limit: number;
  remote_utility_id: number;
  config_level: string;
  show_inactive?: boolean;
  search_data?: string;
  created_by?: string;
  tab?: string; // Added to handle different tabs
}

interface ServiceAreaTableProps {
  entityType: string;
  configLevel: string;
  onEditEntity: (entity: ServiceAreaDataItem) => void;
}

const ServiceAreaTable: React.FC<ServiceAreaTableProps> = ({
  entityType,
  configLevel,
  onEditEntity,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();
  const itemsPerPage = PAGE_SIZE;
  const [selectedEntity, setSelectedEntity] = useState<ServiceAreaItem | null>(
    null
  );
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const navigate = useNavigate();

  // Parse filters from URL params
  const getFiltersFromUrl = (): ServiceAreaFilters => {
    const page = parseInt(searchParams.get("page") || "1");
    const urlFilters: ServiceAreaFilters = {
      page,
      limit: itemsPerPage,
      remote_utility_id: Number(remoteUtilityId),
      config_level: configLevel,
      tab: searchParams.get("tab") || "all", // Default to "all" if not set
    };

    // Parse show_inactive
    const showInactiveParam = searchParams.get("show_inactive");
    if (showInactiveParam) {
      urlFilters.show_inactive = showInactiveParam === "true";
    }

    // Parse created_by
    const createdByParam = searchParams.get("created_by");
    if (createdByParam) {
      urlFilters.created_by = createdByParam;
    }

    // Parse search query
    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<ServiceAreaFilters>(getFiltersFromUrl);

  // Update filters when URL changes
  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [searchParams, remoteUtilityId, configLevel]);

  // API call using the existing useConfigMapData hook
  const {
    data,
    isLoading: loading,
    error,
  } = useConfigMapData(
    remoteUtilityId,
    "TERRITORY_JSON",
    configLevel,
    "",
    null,
    true,
    filters
  );

  const handleApplyFilters = (newFilters: Partial<ServiceAreaFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("show_inactive");
    params.delete("created_by");
    params.delete("page");

    // Apply new filters
    if (newFilters.show_inactive !== undefined) {
      params.set("show_inactive", newFilters.show_inactive.toString());
    }

    if (newFilters.created_by) {
      params.set("created_by", newFilters.created_by);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    params.set("limit", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("show_inactive");
    params.delete("created_by");
    params.delete("page");
    params.delete("query");

    setSearchParams(params);
  };

  // Handle status toggle - for now just console log
  const patchConfigMapMutation = usePatchConfigMap();
  const handleStatusToggle = async (row: any) => {
    try {
      const payload = {
        mapType: "TERRITORY_JSON",
        code: row.code, // required to identify the entity
        remote_utility_id: remoteUtilityId, // fallback if needed
        is_active: !row.isActive, // toggle current status - fixed to use is_active from ServiceAreaItem
      };
      console.log("Toggling status with payload:", payload);
      await patchConfigMapMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Failed to toggle status:", error);
    }
  };

  // Handle view entity
  const handleViewEntity = (entity: ServiceAreaDataItem) => {
    console.log("entity", entity);
    navigate(
  `/utility/utility-setup/service-area/view/${encodeURIComponent(entity.code)}/?tab=${filters["tab"]}`,
  {
    state: { data: entity },
  }
);

  };

  // Helper function to get table columns based on entity type and config level
  const getTableColumns = (): TableColumn<ServiceAreaDataItem>[] => {
    // Base columns that appear in all tables
    const baseColumns: TableColumn<ServiceAreaDataItem>[] = [
      {
        key: "name",
        header: `${entityType} Name`,
        width: "w-64",
        type: "custom",
        render: (_: any, row: ServiceAreaDataItem) => (
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <div>
              <div className="text-xs font-medium text-gray-900">
                {row.name}
              </div>
              <div className="text-xs text-gray-500">
                Code: {row.configurationCode || "NA"}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "isActive", // Fixed to match API field name
        header: "Status",
        width: "w-32",
        type: "custom",
        render: (value: boolean, row: any) => (
          <div className="flex items-center gap-3">
            <Switch
              checked={value}
              onCheckedChange={() => handleStatusToggle(row)}
            />
            <Badge
              variant={value ? "default" : "secondary"}
              className={
                value
                  ? "bg-green-100 text-green-800 border-green-200"
                  : "bg-gray-100 text-gray-800 border-gray-200"
              }
            >
              {value ? "Active" : "Inactive"}
            </Badge>
          </div>
        ),
      },
      {
        key: "createdBy", // Fixed to match API field name
        header: "Created By",
        width: "w-32",
        type: "custom",
        render: (value: string) => (
          <div className="flex items-center gap-1">
            <User className="w-3 h-3 text-gray-400" />
            <span className="text-xs text-gray-900">{value}</span>
          </div>
        ),
      },
    ];

    // Config-specific columns
    const configSpecificColumns: TableColumn<ServiceAreaDataItem>[] = [];

    // Add different columns based on config level
    switch (configLevel) {
      case "zone":
        configSpecificColumns.push({
          key: "childCount",
          header: "Child Count",
          width: "w-24",
          type: "custom",
          render: (value: number) => (
            <span className="text-xs text-gray-600">{value}</span>
          ),
        });
        break;
      case "division":
        configSpecificColumns.push({
          key: "childCount",
          header: "Child Count",
          width: "w-24",
          type: "custom",
          render: (value: number) => (
            <span className="text-xs text-gray-600">{value}</span>
          ),
        });
        break;
      case "area":
        configSpecificColumns.push({
          key: "childCount",
          header: "Child Count",
          width: "w-24",
          type: "custom",
          render: (value: number) => (
            <span className="text-xs text-gray-600">{value}</span>
          ),
        });
        break;
      case "sub_area":
        configSpecificColumns.push({
          key: "childCount",
          header: "Child Count",
          width: "w-24",
          type: "custom",
          render: (value: number) => (
            <span className="text-xs text-gray-600">{value}</span>
          ),
        });
        break;
      case "premise":
        configSpecificColumns.push(
          {
            key: "totalUnits",
            header: "Units",
            width: "w-24",
            type: "custom",
            render: (value: string) => (
              <span className="text-xs text-gray-600">{value || "NA"}</span>
            ),
          },
          {
            key: "installedMeterCount",
            header: "Meters",
            width: "w-32",
            type: "custom",
            render: (value: number) => (
              <span className="text-xs text-gray-600">{value || "NA"}</span>
            ),
          }
        );
        break;
      default:
        // For region, country, state, county - show child count generically
        configSpecificColumns.push({
          key: "childCount",
          header: "Child Count",
          width: "w-24",
          type: "custom",
          render: (value: number) => (
            <span className="text-xs text-gray-600">{value}</span>
          ),
        });
        break;
    }

    // Common columns that appear at the end
    const endColumns: TableColumn<ServiceAreaDataItem>[] = [
      {
        key: "createdDate", // Fixed to match API field name
        header: "Created On",
        width: "w-32",
        type: "custom",
        render: (value: string) => (
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3 text-gray-400" />
            <span className="text-xs text-gray-600">
              {value ? value : "-"}
            </span>
          </div>
        ),
      },
      {
        key: "actions",
        header: "Actions",
        sortable: false,
        width: "w-32",
        type: "custom",
        render: (_: any, row: ServiceAreaDataItem) => (
          <div className="flex gap-2">
            <button
              onClick={() => handleViewEntity(row)}
              className="text-blue-600 hover:text-blue-900 transition-colors"
              title="View Details"
            >
              <Eye className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ];

    return [...baseColumns, ...configSpecificColumns, ...endColumns];
  };

  const columns = getTableColumns();

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  // Handle search
  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);
    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }
    // Reset to page 1 when searching
    params.set("page", "1");
    setSearchParams(params);
  };

  // Since we're using server-side filtering now, we don't need client-side filtering
  // The API should handle search_data and other filters
  const currentPage = parseInt(searchParams.get("page") || "1");

  // Use the data directly from API (it should already be filtered)

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div >
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          {entityType} Management
        </h2>
        <p className="text-xs text-gray-600">
          Manage and view all {entityType} entities
        </p>
      </div>

      {/* Generic Table */}
      <GenericTable
        data={data?.result || []}
        columns={columns}
        searchPlaceholder={`Search ${entityType}...`}
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        rowKey="code"
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil((data?.count || 0) / itemsPerPage),
          totalItems: data?.count || 0,
          itemsPerPage,
          startItem: (currentPage - 1) * itemsPerPage + 1,
          endItem: Math.min(currentPage * itemsPerPage, data?.count || 0),
        }}
        onPageChange={handlePageChange}
        filterPanel={
          <ServiceAreaFilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            entityType={entityType}
          />
        }
        emptyMessage={`No ${entityType} found`}
      />
    </div>
  );
};

export default ServiceAreaTable;
